import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useMyInstructing } from '@/lib/queries';
import { BATCH_COLUMNS, parseTo, type EmailBatch } from '@/lib/email';
import { dateTime } from '@/lib/format';
import { ErrorNote, Spinner } from '@/components/Layout';
import { EmailCompose } from '@/components/EmailCompose';

/** An instructor writes to everyone at their dojo, or to one student. */
export function DojoEmail() {
  const { session } = useAuth();
  const mine = useMyInstructing();
  const [params] = useSearchParams();
  if (mine.isLoading) return <Spinner />;
  const dojos = (mine.data ?? []).map(i => i.dojo).filter(Boolean).map(d => ({ id: d!.id, name: d!.short_name ?? d!.name }));
  if (!dojos.length) return <Navigate to="/home" replace />;
  const to = parseTo(params.get('to'));
  const given = to && (to.kind === 'dojo' || to.kind === 'person') ? to : null;
  return <EmailCompose mode="instructor" given={given} myDojos={dojos} replyTo={session?.user.email ?? null}
    back={{ to: '/dojo', label: 'My dojo' }} sentLink={id => `/dojo/email/sent/${id}`} />;
}

/** One email an instructor sent: what it said and who it went to. */
export function DojoEmailSent() {
  const { id = '' } = useParams();
  const batch = useQuery({
    queryKey: ['email-batch', id],
    queryFn: async () => { const { data, error } = await supabase.from('email_batch').select(BATCH_COLUMNS).eq('id', id).single(); if (error) throw error; return data as EmailBatch; },
  });
  const rows = useQuery({
    queryKey: ['email-batch-rows', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('email_log').select('id,to_email,status').eq('batch_id', id).order('to_email');
      if (error) throw error;
      return data as { id: string; to_email: string; status: string }[];
    },
  });
  if (batch.isLoading || rows.isLoading) return <Spinner />;
  if (batch.error || !batch.data) return <ErrorNote>Could not load this email.</ErrorNote>;
  const b = batch.data;
  return (
    <div className="space-y-5">
      <Link to="/dojo" className="text-sm text-ink-500 hover:underline">← My dojo</Link>
      <section className="card space-y-2">
        <h2 className="text-xl font-bold">{b.subject}</h2>
        <p className="text-sm text-ink-500">Sent {dateTime(b.created_at)} to {b.audience_label} · {b.sent_count} of {b.recipient_count} sent{b.failed_count > 0 && `, ${b.failed_count} failed`}{b.reply_to && ` · replies to ${b.reply_to}`}</p>
        <pre className="whitespace-pre-wrap border-l-2 border-stone-200 pl-3 font-sans text-[15px] text-ink-700">{b.body_text}</pre>
      </section>
      <ul className="card divide-y divide-stone-100 p-0 text-sm">
        {(rows.data ?? []).map(r => <li key={r.id} className="flex justify-between px-5 py-2"><span>{r.to_email}</span><span className={r.status === 'failed' ? 'text-brand-700' : 'text-ink-500'}>{r.status}</span></li>)}
      </ul>
    </div>
  );
}
